import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactSession } from "../../lib/secure_reactsession";
import { getTesterCreditSummaryService } from "../../services/credits";
import {
    requesttesterpayout,
    getLoadMoreWalletHistory,
    getProfile,
} from "../../services/user";
import { LoadingIcon } from "../loader/loadingIcon";
import { roundNumber, showToast } from "../../lib/helpers";
import { LayoutTester } from "../themes/userq/Layouts/layout_tester";
import { PayoutOverviewBox } from "./components/payout_overview_box";
import { Text } from "../themes/userq/Text/Text";
import { Icon } from "../themes/userq/Icon/Icon";
import { format } from "date-fns";
import { Button } from "../themes/userq/Button/Button";
import { useResetRecoilState, atom } from "recoil";
import { ScoreChip } from "../themes/userq/ScoreChip/ScoreChip";
import { VerifyId } from "../idVerification/VerifyId";
import DocumentNotMatch from "../themes/userq/Modal/DocumentNotMatch";
import { ExpiryId } from "../idVerification/ExpiryId";

import AddGiftCardModal from "./components/add-gift-card.js";
import InnerNavigationTab from "../themes/userq/InnernavigationTab/innerNavigationTab.js";

export default function Wallet() {
    const verifyIdRef = useRef(null);

    const scrollContainerRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [minimumThresholdsForPayouts, setMinimumThresholdsForPayouts] =
        useState(0);
    const [payoutHistory, setPayoutHistory] = useState([]);
    const [loadMoreLoading, setLoadMoreLoading] = useState(false);

    const [metaData, setMetaData] = useState({
        sort_status: "",
        sort_date: "",
        sort_method: "",
        load_more: false,
        page: 1,
    });

    const navigate = useNavigate();

    const [credits, setCredits] = useState(null);
    const [requestedPayout, setRequestedPayout] = useState(null);
    const [depositedPayout, setDepositedPayout] = useState(null);

    const [isProfileExpiredAt, setIsProfileExpiredAt] = useState("");

    const creditsState = atom({
        key: "credits",
        default: 0,
    });
    const reset = useResetRecoilState(creditsState);
    const [userBlocked, setUserBlocked] = useState(false);

    const accountstatus = ReactSession.get("accountstatus");
    let score = ReactSession.get("testerscore");

    const [isProfileVerified, setIsProfileVerified] = useState("");
    const [isProfileVerifiedAt, setIsProfileVerifiedAt] = useState("");
    const [isProfileAttempts, setIsProfileAttempts] = useState(0);

    const [documentNotMatch, setDocumentNotMatch] = useState({ open: false });

    const [openGiftCardModal, setOpenGiftCardModal] = useState(false);
    const [isPayoutEnable, setPayoutEnable] = useState(false);
    const [userEmail, setUserEmail] = useState("");

    const handlePaymentModal = (modal) => {
        if (isProfileVerified !== "verified") {
            verifyIdVerificationHandler();
            return false;
        }

        if (isProfileExpiredAt !== "" && isProfileExpiredAt === 0) {
            showToast(
                "Please update your expired ID document before requesting a new payout.",
                "error"
            );
            return false;
        }

        if (
            (credits >= minimumThresholdsForPayouts ||
                requestedPayout + depositedPayout >= minimumThresholdsForPayouts) &&
            credits >= minimumThresholdsForPayouts
        ) {
        } else {
            showToast("You can request payout after earning $30", "error");
            return false;
        }

        if (modal === "giftcard") {
            setOpenGiftCardModal(true);
        }
    };

    useEffect(() => {
        if (
            credits >= minimumThresholdsForPayouts &&
            isProfileVerified == "verified" &&
            !userBlocked
        ) {
            setPayoutEnable(true);
        } else {
            setPayoutEnable(false);
        }
    }, [credits, userBlocked, isProfileVerified]);

    useEffect(() => {
        document.title = "Your wallet | " + process.env.REACT_APP_NAME;

        fetchCreditsSummary();
        getPayoutHistory();
        getProfieData();
    }, []);

    const handleScroll = () => {
        const scrolled = scrollContainerRef.current.scrollTop > 0;
        setIsScrolled(scrolled);
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll);

            return () => {
                scrollContainer.removeEventListener("scroll", handleScroll);
            };
        }
    }, [payoutHistory]);

    const getProfieData = () => {
        let token = ReactSession.get("token");
        setLoading(true);

        getProfile(token).then((response) => {
            setLoading(false);

            if (ReactSession.get("document_not_matched_model")) {
                setDocumentNotMatch({
                    open: true,
                });
                ReactSession.set("document_not_matched_model", false);
            }

            if (response.success) {
                setIsProfileVerified(response.data.user.profile_verification_status);
                setIsProfileVerifiedAt(response.data.user.verified_at);

                setIsProfileExpiredAt(response.data.user.document_expiry_date_left);
                setIsProfileAttempts(response.data.user.expired_document_attempt);
                setUserEmail(response.data.user.email);
            }
        });
    };

    const sortData = (type) => {
        if (type === "status") {
            if (metaData.sort_status === "asc") {
                sortDataApi("status", "desc");
            } else {
                sortDataApi("status", "asc");
            }
        } else if (type === "date") {
            if (metaData.sort_date === "asc") {
                sortDataApi("date", "desc");
            } else {
                sortDataApi("date", "asc");
            }
        } else if (type == "method") {
            if (metaData.sort_method === "asc") {
                sortDataApi("payout_method", "desc");
            } else {
                sortDataApi("payout_method", "asc");
            }
        }
    };

    const sortDataApi = (sortColumn, orderBy) => {
        let token = ReactSession.get("token");
        getLoadMoreWalletHistory(
            {
                page: 1,
                sort_column: sortColumn,
                order_by: orderBy,
            },
            token
        ).then((response) => {
            if (response.success) {
                setPayoutHistory(response.payoutHistory);

                if (sortColumn === "status") {
                    if (orderBy === "desc") {
                        setMetaData({
                            ...metaData,
                            sort_status: "desc",
                            sort_date: "",
                            sort_method: "",
                            page: 1,
                            load_more: response.more_records,
                        });
                    } else {
                        setMetaData({
                            ...metaData,
                            sort_status: "asc",
                            sort_method: "",
                            sort_date: "",
                            page: 1,
                            load_more: response.more_records,
                        });
                    }
                } else if (sortColumn === "date") {
                    if (orderBy === "desc") {
                        setMetaData({
                            ...metaData,
                            sort_status: "",
                            sort_method: "",
                            sort_date: "desc",
                            page: 1,
                            load_more: response.more_records,
                        });
                    } else {
                        setMetaData({
                            ...metaData,
                            sort_status: "",
                            sort_method: "",
                            sort_date: "asc",
                            page: 1,
                            load_more: response.more_records,
                        });
                    }
                } else if (sortColumn === "payout_method") {
                    if (orderBy === "desc") {
                        setMetaData({
                            ...metaData,
                            sort_status: "",
                            sort_date: "",
                            sort_method: "desc",
                            page: 1,
                            load_more: response.more_records,
                        });
                    } else {
                        setMetaData({
                            ...metaData,
                            sort_status: "",
                            sort_date: "",
                            sort_method: "asc",
                            page: 1,
                            load_more: response.more_records,
                        });
                    }
                }
            } else {
                showToast(response.message, "error");
            }
        });
    };

    const loadMore = () => {
        setLoadMoreLoading(true);
        let token = ReactSession.get("token");
        getLoadMoreWalletHistory(
            {
                page: metaData.page + 1,
                sort_column: metaData.sort_status ? "status" : "date",
                order_by: metaData.sort_status ? metaData.sort_status : "desc",
            },
            token
        ).then((response) => {
            setLoadMoreLoading(false);

            if (response.success) {
                setPayoutHistory(payoutHistory.concat(response.payoutHistory));
                setMetaData({
                    ...metaData,
                    page: metaData.page + 1,
                    load_more: response.more_records,
                });
            } else {
                showToast(response.message, "error");
            }
        });
    };

    const fetchCreditsSummary = () => {
        if (!credits) {
            let token = ReactSession.get("token");

            setIsLoading(true);

            getTesterCreditSummaryService(token).then((response) => {
                setIsLoading(false);
                if (response.success) {
                    ReactSession.set("credits", response.summary.credits_balance);
                    if (response.account_status == "blocked") {
                        setUserBlocked(true);
                    }
                    setCredits(response.summary.credits_balance);
                    setRequestedPayout(response.summary.requestedpayoutamount);
                    setDepositedPayout(response.summary.depositedpayoutamount);
                }
            });
        }
    };

    const requestpayout = (method) => {
        if (loading) {
            return;
        }
        if (isProfileVerified !== "verified") {
            verifyIdVerificationHandler();
            return false;
        }

        if (isProfileExpiredAt !== "" && isProfileExpiredAt === 0) {
            showToast(
                "Please update your expired ID document before requesting a new payout.",
                "error"
            );
            return false;
        }

        if (
            (credits >= minimumThresholdsForPayouts ||
                requestedPayout + depositedPayout >= minimumThresholdsForPayouts) &&
            credits >= minimumThresholdsForPayouts
        ) {
        } else {
            showToast("You can request payout after earning $30", "error");
            return false;
        }

        setLoading(true);

        const toast_message =
            "Your payout request has been submitted successfully! You will receive the digital gift card to your registered email within 2 business days. If there is an issue, we will contact you via email.";

        const token = ReactSession.get("token");
        var data = {};
        data["amount"] = credits;
        data["payout_method"] = method;

        requesttesterpayout(data, token).then((response) => {
            setLoading(false);
            if (response.success) {
                setCredits(response.balance);
                setRequestedPayout(response.requestedpayoutamount);
                setDepositedPayout(response.depositedpayoutamount);
                reset();
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "request_payout",
                    method: response.type,
                    value: response.requestedpayoutamount,
                    userID: ReactSession.get("user")
                        ? ReactSession.get("user").user_id
                        : "",
                });
                getPayoutHistory();
                setOpenGiftCardModal(false);

                showToast(toast_message, "success");
            } else {
                if ("error_code" in response) {
                    if (response.error_code == "amount validation failed") {
                        showToast(
                            <>
                                Gift cards cannot be issued for orders above $2000. Please
                                contact{" "}
                                <a href="mailto://support@userq.com">support@userq.com</a> for
                                assistance.
                            </>,
                            "error"
                        );
                    } else if (response.error_code == "insufficient funds") {
                        showToast(
                            <>
                                Something went wrong on our end. Please contact{" "}
                                <a href="mailto://support@userq.com">support@userq.com</a> for
                                assistance.
                            </>,
                            "error"
                        );
                    } else {
                        showToast(response.message, "error");
                    }
                } else {
                    showToast(response.message, "error");
                }
            }
        });
    };

    const getPayoutHistory = () => {
        const token = ReactSession.get("token");
        getLoadMoreWalletHistory(
            {
                page: 1,
                sort_column: "date",
                order_by: "desc",
            },
            token
        ).then((response) => {
            if (response.success) {
                setPayoutHistory(response.payoutHistory);

                setMetaData({
                    sort_status: "",
                    sort_date: "",
                    sort_method: "",
                    load_more: response.result_count > 10 ? true : false,
                    page: 1,
                });

                setMinimumThresholdsForPayouts(
                    parseInt(response.minimum_thresholds_for_payouts)
                );
            } else {
                showToast(response.message, "error");
            }
        });
    };

    const verifyIdVerificationHandler = () => {
        if (
            isProfileVerified === "id_not_matched_profile" ||
            isProfileVerified === "unverified" ||
            (isProfileVerified === "expired_document" && isProfileExpiredAt === "") ||
            isProfileVerified === "invalid_document"
        ) {
            verifyIdRef.current.handleToastMessage(
                isProfileVerified,
                isProfileVerifiedAt
            );
        }
    };

    return (
        <LayoutTester
            isLoading={false}
            skipCheck={false}
            isAccount={true}
            extendedFooter={false}
            activeMenu={"wallet"}
            wrapClass={"xxl-px-240"}
        >
            <div className="credits-page-wrapper wallet-page-wrapper page-min-height">
                {accountstatus == "blocked" && (
                    <div className="account-blocked-message">
                        <div>
                            <Icon
                                colorClass={"danger-red-200"}
                                value={"blocked"}
                                size="large"
                            />
                        </div>
                        <div>
                            <Text
                                type={"subtitle-1"}
                                children="Your payout cannot be redeemed as your profile is currently blocked."
                            />
                            <Text type={"body-text-2"}>
                                Please contact{" "}
                                <a
                                    className="faq-support-link disabled-support-link"
                                    href={
                                        process.env.REACT_APP_URL +
                                        "t/support/contact?reason=Blocked profile"
                                    }
                                    target="_blank"
                                >
                                    UserQ support
                                </a>
                                , to guide you through the next steps.
                            </Text>
                        </div>
                    </div>
                )}
                <div className="page-heading-wrap accounts-page--heading">
                    <Text type="h1">Account</Text>
                    <div className="accounts-score-chip">
                        <ScoreChip testerscore={score} />
                        <div className="account-guidelines-link">
                            <Text type="body-text-3">
                                Read more about{" "}
                                <a
                                    className="faq-support-link"
                                    href={process.env.REACT_APP_URL + "t/tester-guidelines"}
                                    target="_blank"
                                >
                                    tester guidelines
                                </a>
                            </Text>
                        </div>
                    </div>
                </div>
                <InnerNavigationTab
                    type="link"
                    activeMenu={"wallet"}
                    cssClasses={"mb-32 "}
                    items={[
                        { key: "profile", label: "Your profile", link: "/t/profile" },
                        { key: "tests", label: "Test history", link: "/t/test-history" },
                        { key: "wallet", label: "Wallet", link: "/t/wallet" },
                        {
                            key: "settings",
                            label: "Notification settings",
                            link: "/t/settings",
                        },
                    ]}
                />

                <div className="profile-info-section d-block pb-64 pt-64 wallet-page-data">
                    <div className="data-container">
                        {isLoading ? (
                            <div className="accountloading-box">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <>
                                <div className="wallet-top-wrap mt-40">
                                    {(isProfileVerified === "id_not_matched_profile" ||
                                        isProfileVerified === "unverified" ||
                                        (isProfileVerified === "expired_document" &&
                                            isProfileExpiredAt === "") ||
                                        isProfileVerified === "invalid_document") && (
                                            <div className="idverify-message mb-32">
                                                <VerifyId
                                                    ref={verifyIdRef}
                                                    isType={"banner"}
                                                    isProfileVerifiedAt={isProfileVerifiedAt}
                                                    isProfileVerified={isProfileVerified}
                                                    confirmHandler={(status) => {
                                                        if (status) {
                                                            navigate("/dashboard");
                                                        } else {
                                                            navigate("/t/profile");
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}

                                    {(isProfileVerified === "verified" ||
                                        isProfileVerified === "expired_document") &&
                                        isProfileExpiredAt !== "" &&
                                        isProfileExpiredAt >= 0 && (
                                            <div className="idexpiry-message mb-32">
                                                <ExpiryId
                                                    profileExpiredDate={isProfileExpiredAt}
                                                    isProfileAttempts={isProfileAttempts}
                                                    isProfileVerifiedAt={isProfileVerifiedAt}
                                                    confirmHandler={() => {
                                                        navigate("/t/profile");
                                                    }}
                                                />
                                            </div>
                                        )}
                                    <div className="row">
                                        <div className="col-lg-4 col-md-4">
                                            {userBlocked ? (
                                                <PayoutOverviewBox
                                                    amount={roundNumber(credits, 2)}
                                                    type="balance"
                                                    request_payout_enabled={false}
                                                    onClick={() => { }}
                                                    loading={loading}
                                                />
                                            ) : (
                                                <PayoutOverviewBox
                                                    amount={roundNumber(credits, 2)}
                                                    type="balance"
                                                    request_payout_enabled={
                                                        (credits >= minimumThresholdsForPayouts ||
                                                            requestedPayout + depositedPayout >=
                                                            minimumThresholdsForPayouts) &&
                                                        credits >= minimumThresholdsForPayouts
                                                    }
                                                    onClick={requestpayout}
                                                    loading={loading}
                                                />
                                            )}
                                        </div>
                                        <div className="col-lg-4 col-md-4">
                                            <PayoutOverviewBox
                                                amount={roundNumber(requestedPayout, 2)}
                                                type="inprogress"
                                            />
                                        </div>

                                        <div className="col-lg-4 col-md-4">
                                            <PayoutOverviewBox
                                                amount={roundNumber(depositedPayout, 2)}
                                                type="deposited"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-40">
                                    <div className="payment-method-heading">
                                        <Text type={"subtitle-2"} fontWeight={"semi-bold-font"}>
                                            Choose payout method
                                        </Text>
                                        <Text
                                            type={"body-text-2"}
                                            fontWeight={"medium-font"}
                                            cssClasses={"gray-text mt-12"}
                                        >
                                            You can request a payout once your wallet balance reaches
                                            a minimum of $30.
                                        </Text>
                                    </div>
                                    <div className="mt-32 payment-methods-wrapper">
                                        <div className="payment-method-box">
                                            <div className="payment-box-img">
                                                <img
                                                    src={
                                                        process.env.REACT_APP_URL +
                                                        "img/payment-cards-latest.png"
                                                    }
                                                    alt="img"
                                                    className="retailer-logos-image"
                                                />
                                            </div>
                                            <div className="payment-box-content">
                                                <Text type={"subtitle-2"} fontWeight={"semi-bold-font"}>
                                                    Digital gift card
                                                </Text>
                                                <div className="d-flex align-items-center  mt-12">
                                                    <Icon
                                                        value={"time"}
                                                        color={"gray-700-svg"}
                                                        cssClasses={"mr-8"}
                                                    />
                                                    <Text
                                                        type={"body-text-3"}
                                                        fontWeight={"medium-font"}
                                                        cssClasses={"gray-text"}
                                                    >
                                                        Within 2 business days
                                                    </Text>
                                                </div>
                                                <Text type={"caption"} cssClasses={"mt-20 d-block"}>
                                                    Choose from a variety of retailers
                                                </Text>
                                                <Button
                                                    onClick={() => {
                                                        handlePaymentModal("giftcard");
                                                    }}
                                                    type={"primary"}
                                                    size={"small"}
                                                    cssclass={
                                                        "mt-20 btn-block justify-content-center DigitalCardBtn"
                                                    }
                                                    label={"Request payout"}
                                                    state={isPayoutEnable ? "active" : "disabled"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-wrapper study-history-table wallet-payout-history-table mt-32">
                                    {payoutHistory.length > 0 && (
                                        <Text type={"h4"}>Payouts history</Text>
                                    )}

                                    <div
                                        ref={scrollContainerRef}
                                        className={`max-height-table  ${isScrolled ? "scrolled" : ""
                                            }  ${payoutHistory && payoutHistory.length > 8
                                                ? "responsive-table-scroll"
                                                : ""
                                            }`}
                                    >
                                        <table className="table">
                                            {payoutHistory.length > 0 ? (
                                                <thead>
                                                    <tr>
                                                        <th>Amount</th>
                                                        <th className="">
                                                            <div className="table-center-div">
                                                                <div
                                                                    onClick={() => {
                                                                        sortData("status");
                                                                    }}
                                                                    className={`${metaData.sort_status
                                                                            ? metaData.sort_status
                                                                            : ""
                                                                        }  col-sort justify-content-between`}
                                                                >
                                                                    Status
                                                                    <span>
                                                                        {metaData.sort_status == "asc" && (
                                                                            <Icon value={"sortup"} />
                                                                        )}
                                                                        {metaData.sort_status == "desc" && (
                                                                            <Icon value={"sortdown"} />
                                                                        )}
                                                                        {!metaData.sort_status && (
                                                                            <Icon value={"sort-icon"} />
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div
                                                                onClick={() => {
                                                                    sortData("method");
                                                                }}
                                                                className={`${metaData.sort_method
                                                                        ? metaData.sort_method
                                                                        : ""
                                                                    }  col-sort justify-content-between`}
                                                            >
                                                                Payout Method
                                                                <span>
                                                                    {metaData.sort_method == "asc" && (
                                                                        <Icon value={"sortup"} />
                                                                    )}
                                                                    {metaData.sort_method == "desc" && (
                                                                        <Icon value={"sortdown"} />
                                                                    )}
                                                                    {!metaData.sort_method && (
                                                                        <Icon value={"sort-icon"} />
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div
                                                                onClick={() => {
                                                                    sortData("date");
                                                                }}
                                                                className={`${metaData.sort_date ? metaData.sort_date : ""
                                                                    }  col-sort justify-content-between`}
                                                            >
                                                                Date
                                                                <span>
                                                                    {metaData.sort_date == "asc" && (
                                                                        <Icon value={"sortup"} />
                                                                    )}
                                                                    {metaData.sort_date == "desc" && (
                                                                        <Icon value={"sortdown"} />
                                                                    )}
                                                                    {!metaData.sort_date && (
                                                                        <Icon value={"sort-icon"} />
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                            ) : (
                                                ""
                                            )}
                                            <tbody>
                                                {payoutHistory.map((result, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td>${roundNumber(result.amount, 2)}</td>
                                                            <td className="">
                                                                <div className="table-center-div">
                                                                    <div className="active-tiles-wrap">
                                                                        {result.status === "deposited" && (
                                                                            <span className={"completed"}>
                                                                                Completed
                                                                            </span>
                                                                        )}
                                                                        {result.status === "in-progress" && (
                                                                            <span className={"in-progress"}>
                                                                                In-progress
                                                                            </span>
                                                                        )}
                                                                        {result.status === "cancelled" && (
                                                                            <span className={"cancelled"}>
                                                                                Cancelled
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{result.payout_method}</td>

                                                            <td>
                                                                {format(
                                                                    new Date(result.created_at),
                                                                    "dd/MM/yy"
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>

                                        {metaData.load_more && (
                                            <div className={"load-more-data"}>
                                                <Button
                                                    type="primary"
                                                    size={`large`}
                                                    label="Load more"
                                                    microLoading={loadMoreLoading}
                                                    onClick={() => {
                                                        loadMore();
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <DocumentNotMatch
                confirm_title={"We need to update your profile to match your ID"}
                openModal={documentNotMatch.open}
                close={() => {
                    setDocumentNotMatch({ open: false });
                }}
                confirmHandler={(status) => {
                    if (status) {
                        navigate("/dashboard");
                    } else {
                        navigate("/t/profile");
                    }
                }}
            />
            <AddGiftCardModal
                openModal={openGiftCardModal}
                closeModal={() => {
                    setOpenGiftCardModal(false);
                }}
                amount={roundNumber(credits, 2)}
                requestPayout={() => requestpayout("Gift card")}
                isLoading={loading}
                userEmail={userEmail}
            />
        </LayoutTester>
    );
}
